import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import LocationPicker from "@/components/LocationPicker";
import { getAuthSession, getValidAuthSession } from "@/lib/supabaseAuth";
import { fetchSupabaseRows, updateSupabaseRows } from "@/lib/supabaseRest";
import { reviewPropertySubmission } from "@/lib/propertySubmissionAgent";

const propertyTypeOptions = [
  { value: "economico", label: "Accesible" },
  { value: "local", label: "Local" },
  { value: "bodega", label: "Bodega" },
  { value: "terreno", label: "Terreno" },
  { value: "oficina", label: "Oficina / consultorio" },
  { value: "casa", label: "Casa para negocio" },
];

const serviceOptions = [
  "Luz",
  "Agua",
  "Internet",
  "Drenaje",
  "Baño",
  "Aire acondicionado",
];

const availabilityOptions = ["Inmediata", "En 15 días", "En 30 días", "Otro"];

const parseMeasure = (value) => {
  const match = (value || "").toString().replace(",", ".").match(/[\d.]+/);
  return match ? Number(match[0]) : 0;
};

const formatSquareMeters = (value) => {
  if (!Number.isFinite(value) || value <= 0) return "";
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const SectionTitle = ({ title, children }) => (
  <div className="border-b border-slate-100 pb-3">
    <h2 className="text-lg font-black text-slate-950">{title}</h2>
    {children && <p className="mt-1 text-sm leading-6 text-slate-600">{children}</p>}
  </div>
);

const FieldShell = ({ label, help, children }) => (
  <label className="block">
    <span className="text-sm font-black text-slate-900">{label}</span>
    {help && <span className="mt-1 block text-xs leading-5 text-slate-500">{help}</span>}
    <div className="mt-2">{children}</div>
  </label>
);

const uploadFiles = async (files, resourceType = "image") => {
  const urls = [];

  for (const file of files) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "tulocalmx_upload");

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/dz2ylmtpr/${resourceType}/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error("No se pudo subir un archivo.");
    }

    const data = await response.json();
    urls.push(data.secure_url);
  }

  return urls;
};

const rowToForm = (row) => ({
  nombre: row.nombre || "",
  telefono: row.telefono || "",
  correo: row.correo || "",
  precio: row.precio || "",
  ubicacion: row.ubicacion || "",
  calle: row.calle || "",
  numero: row.numero || "",
  colonia: row.colonia || "",
  ciudad: row.ciudad || "",
  codigoPostal: row.codigo_postal || "",
  lat: row.lat || "",
  lng: row.lng || "",
  mapUrl: row.map_url || "",
  tipo: row.tipo || "local",
  descripcion: row.descripcion || "",
  ventajas: row.ventajas || "",
  frente: row.frente || "",
  fondo: row.fondo || "",
  metros: row.metros || "",
  altura: row.altura || "",
  nivel: row.nivel || "",
  estacionamiento: row.estacionamiento || "",
  servicios: row.servicios
    ? row.servicios
        .split(",")
        .map((item) => item.trim())
        .filter((item) => serviceOptions.includes(item))
    : [],
  serviciosOtro: row.servicios
    ? row.servicios
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item && !serviceOptions.includes(item))
        .join(", ")
    : "",
  disponibilidad: availabilityOptions.includes(row.disponibilidad)
    ? row.disponibilidad
    : row.disponibilidad
    ? "Otro"
    : "",
  disponibilidadOtro: availabilityOptions.includes(row.disponibilidad)
    ? ""
    : row.disponibilidad || "",
  contrato: row.contrato || "Mínimo 1 año",
  zonaTipo: row.zona_tipo || "",
  tieneRestricciones:
    row.restricciones && !row.restricciones.startsWith("No hay restricciones")
      ? "si"
      : "no",
  restricciones:
    row.restricciones && !row.restricciones.startsWith("No hay restricciones")
      ? row.restricciones
      : "",
  negociosMismaPlaza: row.negocios_misma_plaza || "",
  plan: row.plan || "gratis",
});

const EditarPropiedad = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(() => getAuthSession());
  const [form, setForm] = useState(null);
  const [images, setImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [newVideos, setNewVideos] = useState([]);
  const [plans, setPlans] = useState([]);
  const [newPlans, setNewPlans] = useState([]);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadProperty = async () => {
    const currentSession = await getValidAuthSession();
    if (isMounted) setSession(currentSession);

    if (!currentSession?.access_token) {
      setIsLoading(false);
      return;
    }

    const query = new URLSearchParams({
      select: "*",
      id: `eq.${id}`,
      limit: "1",
    });

    fetchSupabaseRows("property_submissions", query.toString(), {
      accessToken: currentSession.access_token,
    })
      .then((rows) => {
        const row = rows[0];
        if (!row) {
          setError("No encontramos esta propiedad en tu cuenta.");
          return;
        }

        setForm(rowToForm(row));
        setImages(Array.isArray(row.imagenes) ? row.imagenes : []);
        setVideos(Array.isArray(row.videos) ? row.videos : []);
        setPlans(Array.isArray(row.planos) ? row.planos : []);
      })
      .catch((loadError) => setError(loadError.message))
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    };

    loadProperty();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const updateForm = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "frente" || field === "fondo") {
        const frente = parseMeasure(field === "frente" ? value : next.frente);
        const fondo = parseMeasure(field === "fondo" ? value : next.fondo);
        next.metros = formatSquareMeters(frente * fondo);
      }

      return next;
    });
  };

  const removeImage = (indexToRemove) => {
    setImages((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const removeVideo = (indexToRemove) => {
    setVideos((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const removePlan = (indexToRemove) => {
    setPlans((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const toggleService = (service) => {
    setForm((prev) => {
      const current = Array.isArray(prev.servicios) ? prev.servicios : [];
      const exists = current.includes(service);

      return {
        ...prev,
        servicios: exists
          ? current.filter((item) => item !== service)
          : [...current, service],
      };
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    setMessage("");

    try {
      const latitude = Number(form.lat);
      const longitude = Number(form.lng);
      if (
        !Number.isFinite(latitude) ||
        !Number.isFinite(longitude) ||
        (latitude === 0 && longitude === 0)
      ) {
        throw new Error("Marca la ubicacion exacta en el mapa antes de guardar.");
      }

      const uploadedImages = await uploadFiles(newImages, "image");
      const uploadedVideos = await uploadFiles(newVideos, "video");
      const uploadedPlans = await uploadFiles(newPlans, "auto");
      const finalImages = [...images, ...uploadedImages].slice(0, 10);
      const finalVideos = [...videos, ...uploadedVideos].slice(0, 2);
      const finalPlans = [...plans, ...uploadedPlans].slice(0, 4);
      const serviciosFinal = [
        ...(Array.isArray(form.servicios) ? form.servicios : []),
        form.serviciosOtro.trim(),
      ]
        .filter(Boolean)
        .join(", ");
      const disponibilidadFinal =
        form.disponibilidad === "Otro"
          ? form.disponibilidadOtro.trim()
          : form.disponibilidad;
      const formForReview = {
        ...form,
        servicios: serviciosFinal,
        disponibilidad: disponibilidadFinal,
      };
      const review = reviewPropertySubmission(formForReview, {
        imageUrls: finalImages,
        videoUrls: finalVideos,
        planUrls: finalPlans,
      });
      const restricciones =
        form.tieneRestricciones === "si"
          ? form.restricciones
          : "No hay restricciones reportadas por el anunciante.";

      await updateSupabaseRows(
        "property_submissions",
        `id=eq.${encodeURIComponent(id)}`,
        {
          estado_publicacion: review.estadoPublicacion,
          nombre: form.nombre,
          telefono: form.telefono,
          correo: form.correo,
          precio: form.precio ? Number(form.precio) : null,
          ubicacion: form.ubicacion,
          calle: form.calle,
          numero: form.numero,
          colonia: form.colonia,
          ciudad: form.ciudad,
          codigo_postal: form.codigoPostal,
          lat: latitude,
          lng: longitude,
          map_url: `https://www.google.com/maps?q=${latitude},${longitude}&z=18&output=embed`,
          tipo: form.tipo,
          descripcion: form.descripcion,
          ventajas: form.ventajas,
          frente: form.frente,
          fondo: form.fondo,
          metros: form.metros,
          altura: form.altura,
          nivel: form.nivel,
          estacionamiento: form.estacionamiento,
          servicios: serviciosFinal,
          disponibilidad: disponibilidadFinal,
          contrato: form.contrato,
          zona_tipo: form.zonaTipo,
          restricciones,
          negocios_misma_plaza: form.negociosMismaPlaza,
          imagenes: finalImages,
          videos: finalVideos,
          planos: finalPlans,
          revision_score: review.score,
          revision_alertas: review.alerts,
          publicada_en:
            review.estadoPublicacion === "publicada"
              ? new Date().toISOString()
              : null,
          notas_admin: review.adminNote,
          updated_at: new Date().toISOString(),
        },
        { accessToken: session.access_token }
      );

      const signalResponse = await fetch("/api/sync-advisor-property-signals", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ property_id: id }),
      });

      if (!signalResponse.ok) {
        console.warn("La propiedad se guardo, pero sus senales del asesor no se actualizaron.");
      }

      setImages(finalImages);
      setVideos(finalVideos);
      setPlans(finalPlans);
      setNewImages([]);
      setNewVideos([]);
      setNewPlans([]);
      const savedMessage = review.needsManualReview
        ? "Guardamos los cambios. La propiedad requiere revisión antes de publicarse."
        : "Cambios guardados. La propiedad sigue publicada.";
      setMessage(savedMessage);
      navigate("/mis-propiedades", {
        replace: true,
        state: { propertyUpdateMessage: savedMessage },
      });
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!session?.access_token) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-slate-50 px-4 py-24">
          <div className="mx-auto max-w-md rounded-xl border bg-white p-6 text-center">
            <h1 className="text-xl font-black">Inicia sesión para editar</h1>
            <Link
              to="/iniciar-sesion"
              className="mt-4 inline-flex rounded-lg bg-[#F97316] px-4 py-3 font-black text-white"
            >
              Iniciar sesión
            </Link>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-slate-50 px-4 py-24">
        <div className="mx-auto max-w-4xl">
          <button
            type="button"
            onClick={() => navigate("/mis-propiedades")}
            className="mb-4 text-sm font-black text-[#0056D2]"
          >
            Regresar a mis propiedades
          </button>

          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h1 className="text-2xl font-black text-slate-950">Editar propiedad</h1>
            <p className="mt-2 text-sm text-slate-600">
              Edita cada sección con calma. Si algo queda incompleto o sospechoso, el agente la mandará a revisión.
            </p>

            {isLoading ? (
              <p className="mt-5 text-sm font-semibold text-slate-500">Cargando...</p>
            ) : error && !form ? (
              <p className="mt-5 rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </p>
            ) : (
              <form onSubmit={handleSave} className="mt-6 space-y-7">
                <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
                  <SectionTitle title="Datos de contacto">
                    Estos datos se usan para identificar al anunciante y recibir prospectos.
                  </SectionTitle>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FieldShell label="Nombre del anunciante" help="Persona o empresa que atenderá esta propiedad.">
                      <input required type="text" value={form.nombre} onChange={(e) => updateForm("nombre", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="WhatsApp" help="Número donde sí puedan responder interesados.">
                      <input required type="tel" value={form.telefono} onChange={(e) => updateForm("telefono", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="Correo" help="Aquí llegarán avisos importantes de Tulocalmx.">
                      <input required type="email" value={form.correo} onChange={(e) => updateForm("correo", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="Tipo de propiedad" help="Sirve para mostrarla en la categoría correcta.">
                      <select required value={form.tipo} onChange={(e) => updateForm("tipo", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3">
                        {propertyTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </FieldShell>
                  </div>
                </section>

                <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
                  <SectionTitle title="Datos principales del espacio">
                    Revisa precio, ubicación, medidas y características para que el interesado entienda rápido la propiedad.
                  </SectionTitle>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FieldShell label="Precio mensual" help="Escribe solo números. Si está entre $2,000 y $8,000 se mostrará como Accesible.">
                      <input required type="number" value={form.precio} onChange={(e) => updateForm("precio", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <div className="sm:col-span-2">
                      <LocationPicker
                        address={form.ubicacion}
                        lat={form.lat}
                        lng={form.lng}
                        onAddressChange={(value) => updateForm("ubicacion", value)}
                        onSelect={({ lat, lng }) => {
                          updateForm("lat", lat);
                          updateForm("lng", lng);
                        }}
                        onAddressDetails={(details) => {
                          updateForm("calle", details.calle || "");
                          updateForm("numero", details.numero || "");
                          updateForm("colonia", details.colonia || "");
                          updateForm("ciudad", details.ciudad || "");
                          updateForm("codigoPostal", details.codigoPostal || "");
                        }}
                      />
                    </div>
                    <FieldShell label="Calle" help="Se llena con el pin. Si no coincide, corrígela aquí.">
                      <input required type="text" value={form.calle} onChange={(e) => updateForm("calle", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="Número" help="Si la propiedad no tiene número visible, puedes dejarlo vacío.">
                      <input type="text" value={form.numero} onChange={(e) => updateForm("numero", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="Colonia" help="Este dato ayuda a que la card pública se vea correcta.">
                      <input required type="text" value={form.colonia} onChange={(e) => updateForm("colonia", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="Ciudad" help="Se usa como apoyo para la ubicación pública.">
                      <input type="text" value={form.ciudad} onChange={(e) => updateForm("ciudad", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="Frente" help="Al cambiar frente y fondo se calcula el total.">
                      <input required type="text" value={form.frente} onChange={(e) => updateForm("frente", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="Fondo" help="Medida del fondo del espacio.">
                      <input required type="text" value={form.fondo} onChange={(e) => updateForm("fondo", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="Metros cuadrados" help="Se calcula con frente por fondo para evitar errores.">
                      <input required readOnly type="text" value={form.metros} className="w-full rounded-lg border border-slate-200 bg-slate-100 p-3" />
                    </FieldShell>
                    <FieldShell label="Altura" help="Altura aproximada del local, bodega o espacio.">
                      <input type="text" value={form.altura} onChange={(e) => updateForm("altura", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="Nivel o piso" help="Ejemplo: planta baja, planta alta, segundo piso o a pie de carretera.">
                      <input required type="text" value={form.nivel} onChange={(e) => updateForm("nivel", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                    <FieldShell label="Estacionamiento" help="Indica si tiene cajones propios, compartidos o si no cuenta con estacionamiento.">
                      <input required type="text" value={form.estacionamiento} onChange={(e) => updateForm("estacionamiento", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" />
                    </FieldShell>
                  </div>
                </section>

                <section className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-black text-slate-900">Servicios disponibles</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Marca todo lo que tiene la propiedad y agrega otros servicios si hacen falta.
                    </p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {serviceOptions.map((service) => (
                        <label key={service} className="flex min-w-0 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold leading-tight text-slate-700">
                          <input type="checkbox" checked={form.servicios.includes(service)} onChange={() => toggleService(service)} className="shrink-0" />
                          <span className="min-w-0 break-words">{service}</span>
                        </label>
                      ))}
                    </div>
                    <input type="text" placeholder="Otro servicio o detalle" value={form.serviciosOtro} onChange={(e) => updateForm("serviciosOtro", e.target.value)} className="mt-3 w-full rounded-lg border bg-white p-3" />
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-black text-slate-900">Disponibilidad</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Indica cuándo puede ocuparse la propiedad.
                    </p>
                    <select required value={form.disponibilidad} onChange={(e) => updateForm("disponibilidad", e.target.value)} className="mt-3 w-full rounded-lg border bg-white p-3">
                      <option value="">Elige una opción</option>
                      {availabilityOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {form.disponibilidad === "Otro" && (
                      <input required type="text" placeholder="Escribe la disponibilidad" value={form.disponibilidadOtro} onChange={(e) => updateForm("disponibilidadOtro", e.target.value)} className="mt-3 w-full rounded-lg border bg-white p-3" />
                    )}
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-black text-slate-900">Contrato y zona</p>
                    <input required type="text" placeholder="Contrato" value={form.contrato} onChange={(e) => updateForm("contrato", e.target.value)} className="mt-3 w-full rounded-lg border bg-white p-3" />
                    <input required type="text" placeholder="Zona: Habitacional, Comercial, Industrial o combinada" value={form.zonaTipo} onChange={(e) => updateForm("zonaTipo", e.target.value)} className="mt-3 w-full rounded-lg border bg-white p-3" />
                  </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-black text-slate-900">Restricciones</p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Si el dueño o la plaza no permite algún giro o actividad, selecciónalo y explícalo con claridad.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <label className="text-sm font-bold">
                      <input type="radio" name="restricciones" checked={form.tieneRestricciones === "no"} onChange={() => updateForm("tieneRestricciones", "no")} className="mr-2" />
                      No tiene
                    </label>
                    <label className="text-sm font-bold">
                      <input type="radio" name="restricciones" checked={form.tieneRestricciones === "si"} onChange={() => updateForm("tieneRestricciones", "si")} className="mr-2" />
                      Sí tiene
                    </label>
                  </div>
                  {form.tieneRestricciones === "si" && (
                    <textarea required value={form.restricciones} onChange={(e) => updateForm("restricciones", e.target.value)} className="mt-3 w-full rounded-lg border bg-white p-3" rows="3" />
                  )}
                </section>

                <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
                  <SectionTitle title="Entorno y descripción">
                    Entre más claro quede el entorno y la propiedad, más fácil será que el interesado entienda si le funciona.
                  </SectionTitle>
                  <FieldShell label="Negocios en la misma plaza o alrededor" help="Escribe todo lo que puedas sobre el entorno: negocios cercanos, movimiento de la zona y giros que ya existen.">
                    <textarea required value={form.negociosMismaPlaza} onChange={(e) => updateForm("negociosMismaPlaza", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" rows="3" />
                  </FieldShell>
                  <FieldShell label="Descripción general" help="Describe la propiedad, sus ventajas, estado, accesos, visibilidad y detalles importantes. Mínimo 40 caracteres.">
                    <textarea required minLength={40} value={form.descripcion} onChange={(e) => updateForm("descripcion", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" rows="4" />
                  </FieldShell>
                  <FieldShell label="Ventajas principales" help="Escribe una ventaja por renglón. Recomendamos mínimo 3.">
                    <textarea value={form.ventajas} onChange={(e) => updateForm("ventajas", e.target.value)} className="w-full rounded-lg border border-slate-200 p-3" rows="4" />
                  </FieldShell>
                </section>

                <section className="space-y-5 rounded-xl border border-slate-200 bg-white p-4">
                  <SectionTitle title="Fotos, video y croquis">
                    Actualiza el material visual. Recomendamos mínimo 3 fotos claras: frente, interior y una vista del entorno.
                  </SectionTitle>
                  <div>
                    <p className="mb-2 text-sm font-black text-slate-900">Fotos actuales</p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                      {images.map((image, index) => (
                        <div key={image} className="relative aspect-square overflow-hidden rounded-lg border bg-slate-100">
                          <button type="button" onClick={() => removeImage(index)} className="absolute right-1 top-1 z-10 rounded-full bg-black/70 px-2 py-1 text-xs text-white">x</button>
                          <img src={image} alt="" className="h-full w-full object-cover" />
                        </div>
                      ))}
                    </div>
                    <input type="file" accept="image/*" multiple onChange={(e) => setNewImages(Array.from(e.target.files).slice(0, 10))} className="mt-3 w-full rounded-lg border p-3" />
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      Puedes agregar más fotos o quitar las que ya no representen bien el espacio.
                    </p>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <div>
                      <p className="mb-2 text-sm font-black text-slate-900">Videos actuales</p>
                      <p className="mb-3 text-xs leading-5 text-slate-500">
                        Un video exterior e interior ayuda a que el interesado decida más rápido.
                      </p>
                      <div className="space-y-2">
                        {videos.map((video, index) => (
                          <div key={video} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                            <span className="truncate">{video}</span>
                            <button type="button" onClick={() => removeVideo(index)} className="font-black text-red-600">Quitar</button>
                          </div>
                        ))}
                      </div>
                      <input type="file" accept="video/*" multiple onChange={(e) => setNewVideos(Array.from(e.target.files).slice(0, 2))} className="mt-3 w-full rounded-lg border p-3" />
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-black text-slate-900">Croquis, plano o documento</p>
                      <p className="mb-3 text-xs leading-5 text-slate-500">
                        Si tienes croquis o plano, súbelo para que el interesado entienda mejor la distribución.
                      </p>
                      <div className="space-y-2">
                        {plans.map((plan, index) => (
                          <div key={plan} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                            <span className="truncate">{plan}</span>
                            <button type="button" onClick={() => removePlan(index)} className="font-black text-red-600">Quitar</button>
                          </div>
                        ))}
                      </div>
                      <input type="file" accept="image/*,.pdf" multiple onChange={(e) => setNewPlans(Array.from(e.target.files).slice(0, 4))} className="mt-3 w-full rounded-lg border p-3" />
                    </div>
                  </div>
                </section>

                {error && <p className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">{error}</p>}
                {message && <p className="rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-700">{message}</p>}

                <button type="submit" disabled={isSaving} className="w-full rounded-lg bg-[#F97316] p-4 font-black text-white disabled:bg-orange-300">
                  {isSaving ? "Guardando..." : "Guardar cambios"}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default EditarPropiedad;
